import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Plus, Pencil, Trash2, Boxes, Wallet, TrendingDown, Archive, Search,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtR = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_COLORS = {
  "In Use": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "In Storage": "bg-slate-100 text-slate-700 border-slate-200",
  "Under Repair": "bg-amber-100 text-amber-700 border-amber-200",
  "Disposed": "bg-gray-200 text-gray-600 border-gray-300",
  "Sold": "bg-blue-100 text-blue-700 border-blue-200",
  "Scrapped": "bg-red-100 text-red-700 border-red-200",
};

const blankForm = {
  name: "", category: "Shelving & Fixtures", brand: "", model: "", serial_number: "",
  purchase_date: new Date().toISOString().slice(0, 10),
  supplier: "", reference: "", cost_excl_vat: "", cost_incl_vat: "",
  location: "", status: "In Use", notes: "",
  wear_tear_key: "shop_fittings", useful_life_years: 6, residual_value: 0,
};

export default function AssetManager() {
  const [tab, setTab] = useState("register");
  const [assets, setAssets] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [wearTearRates, setWearTearRates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSearch, setFilterSearch] = useState("");

  // Form / dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadOptions = useCallback(async () => {
    const [c, w] = await Promise.all([
      axios.get(`${API}/assets/options/categories`),
      axios.get(`${API}/assets/options/wear-tear`),
    ]);
    setCategories(c.data.categories || []);
    setStatuses(c.data.statuses || []);
    setWearTearRates(w.data.rates || []);
  }, []);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== "all") params.category = filterCategory;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterSearch.trim()) params.search = filterSearch.trim();
      const res = await axios.get(`${API}/assets`, { params });
      setAssets(res.data.assets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus, filterSearch]);

  const loadDashboard = useCallback(async () => {
    const res = await axios.get(`${API}/assets/dashboard`);
    setDashboard(res.data);
  }, []);

  const loadSchedule = useCallback(async () => {
    const res = await axios.get(`${API}/assets/depreciation-schedule`);
    setSchedule(res.data);
  }, []);

  useEffect(() => { loadOptions(); }, [loadOptions]);
  useEffect(() => { loadAssets(); loadDashboard(); }, [loadAssets, loadDashboard]);
  useEffect(() => { if (tab === "schedule") loadSchedule(); }, [tab, loadSchedule]);

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      name: a.name || "", category: a.category, brand: a.brand || "", model: a.model || "",
      serial_number: a.serial_number || "", purchase_date: a.purchase_date,
      supplier: a.supplier || "", reference: a.reference || "",
      cost_excl_vat: a.cost_excl_vat ?? "", cost_incl_vat: a.cost_incl_vat ?? "",
      location: a.location || "", status: a.status, notes: a.notes || "",
      wear_tear_key: a.wear_tear_key || "shop_fittings",
      useful_life_years: a.useful_life_years ?? 6,
      residual_value: a.residual_value ?? 0,
    });
    setDialogOpen(true);
  };

  const handleWearTearChange = (key) => {
    const meta = wearTearRates.find((w) => w.key === key);
    setForm((f) => ({ ...f, wear_tear_key: key, useful_life_years: meta?.years ?? f.useful_life_years }));
  };

  const handleCostExclChange = (v) => {
    const excl = Number(v) || 0;
    setForm((f) => ({ ...f, cost_excl_vat: v, cost_incl_vat: excl > 0 ? (excl * 1.15).toFixed(2) : "" }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.purchase_date || !form.cost_excl_vat) {
      toast.error("Name, purchase date and cost are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_excl_vat: Number(form.cost_excl_vat),
        cost_incl_vat: form.cost_incl_vat ? Number(form.cost_incl_vat) : undefined,
        useful_life_years: Number(form.useful_life_years),
        residual_value: Number(form.residual_value || 0),
      };
      if (editingId) {
        await axios.put(`${API}/assets/${editingId}`, payload);
        toast.success("Asset updated");
      } else {
        const r = await axios.post(`${API}/assets`, payload);
        toast.success(`Asset created — tag ${r.data.asset_tag}`);
      }
      setDialogOpen(false);
      await Promise.all([loadAssets(), loadDashboard()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/assets/${confirmDelete.id}`);
      toast.success(`Deleted ${confirmDelete.asset_tag}`);
      setConfirmDelete(null);
      await Promise.all([loadAssets(), loadDashboard()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const totalCostAfterFilter = useMemo(
    () => assets.reduce((s, a) => s + (a.cost_excl_vat || 0), 0),
    [assets]
  );
  const totalBookAfterFilter = useMemo(
    () => assets.reduce((s, a) => s + (a.book_value || 0), 0),
    [assets]
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Boxes className="h-7 w-7 text-indigo-600" />
            Asset Manager
          </h1>
          <p className="text-sm text-gray-500">
            Fixed Asset Register with SARS-compliant straight-line depreciation. Feeds the Income Statement automatically.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="asset-create-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Asset
        </Button>
      </div>

      {/* Dashboard tiles */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase"><Boxes className="h-4 w-4" /> Assets</div>
            <div className="text-2xl font-bold text-gray-900">{dashboard.total_assets}</div>
            <div className="text-xs text-gray-500">{dashboard.live_assets} live · {dashboard.disposed_assets} disposed</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase"><Wallet className="h-4 w-4" /> Total cost</div>
            <div className="text-2xl font-bold text-blue-700" data-testid="dashboard-total-cost">{fmtR(dashboard.total_cost_excl_vat)}</div>
            <div className="text-xs text-gray-500">Excl. VAT</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase"><Archive className="h-4 w-4" /> Book value</div>
            <div className="text-2xl font-bold text-emerald-700">{fmtR(dashboard.total_book_value)}</div>
            <div className="text-xs text-gray-500">After depreciation</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase"><TrendingDown className="h-4 w-4" /> Depreciation YTD</div>
            <div className="text-2xl font-bold text-amber-700">{fmtR(dashboard.depreciation_ytd)}</div>
            <div className="text-xs text-gray-500">{fmtR(dashboard.depreciation_this_month)} this month</div>
          </CardContent></Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
          <TabsTrigger value="by-category" data-testid="tab-by-category">By Category</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Depreciation Schedule</TabsTrigger>
        </TabsList>

        {/* REGISTER */}
        <TabsContent value="register" className="space-y-3">
          <Card>
            <CardContent className="p-3 flex flex-wrap items-end gap-2">
              <div className="space-y-1 flex-1 min-w-[200px]">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Name, tag, serial..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    className="pl-8 h-9"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[220px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="py-10 flex items-center justify-center text-gray-500"><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading...</div>
              ) : assets.length === 0 ? (
                <div className="py-10 text-center text-gray-500">No assets recorded yet. Click <strong>Add Asset</strong> to get started.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-24">Tag</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Book value</TableHead>
                      <TableHead className="text-right">Mthly Dep.</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assets.map((a) => (
                      <TableRow key={a.id} data-testid={`asset-row-${a.asset_tag}`}>
                        <TableCell className="font-mono text-xs font-bold text-indigo-700">{a.asset_tag}</TableCell>
                        <TableCell>
                          <div className="font-medium">{a.name}</div>
                          <div className="text-[11px] text-gray-500">{[a.brand, a.model, a.serial_number].filter(Boolean).join(" · ")}</div>
                        </TableCell>
                        <TableCell className="text-xs">{a.category}</TableCell>
                        <TableCell className="text-xs">{a.location || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[a.status] || ""}`}>{a.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtR(a.cost_excl_vat)}</TableCell>
                        <TableCell className={`text-right font-mono text-sm ${a.fully_depreciated ? "text-gray-400 line-through" : "text-emerald-700"}`}>
                          {fmtR(a.book_value)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-amber-700">{fmtR(a.monthly_depreciation)}</TableCell>
                        <TableCell className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`asset-edit-${a.asset_tag}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(a)} data-testid={`asset-delete-${a.asset_tag}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          {assets.length > 0 && (
            <div className="text-right text-sm text-gray-600">
              {assets.length} asset(s) shown — total cost <span className="font-bold text-blue-700">{fmtR(totalCostAfterFilter)}</span> · book value <span className="font-bold text-emerald-700">{fmtR(totalBookAfterFilter)}</span>
            </div>
          )}
        </TabsContent>

        {/* BY CATEGORY */}
        <TabsContent value="by-category">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Book value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(dashboard?.by_category || []).map((r) => (
                    <TableRow key={r.category}>
                      <TableCell className="font-medium">{r.category}</TableCell>
                      <TableCell className="text-right">{r.count}</TableCell>
                      <TableCell className="text-right font-mono">{fmtR(r.cost)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{fmtR(r.book_value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SCHEDULE */}
        <TabsContent value="schedule">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{schedule?.year || ""} Straight-Line Depreciation Schedule</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Depreciation expense</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(schedule?.months || []).map((m) => (
                    <TableRow key={m.month}>
                      <TableCell>{new Date(2000, m.month - 1).toLocaleString("en-ZA", { month: "long" })}</TableCell>
                      <TableCell className="text-right font-mono">{fmtR(m.depreciation)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-amber-50 font-bold">
                    <TableCell>Annual total</TableCell>
                    <TableCell className="text-right font-mono text-amber-700">{fmtR(schedule?.annual_total)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Asset" : "Add New Asset"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label>Asset name <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. HP ProBook Laptop" data-testid="asset-form-name" />
            </div>
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Brand</Label>
              <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Model</Label>
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Serial number</Label>
              <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Purchase date <span className="text-red-500">*</span></Label>
              <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Supplier</Label>
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Reference (GRV# / Invoice#)</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Cash Office" />
            </div>
            <div className="space-y-1">
              <Label>Cost (excl VAT) <span className="text-red-500">*</span></Label>
              <Input type="number" step="0.01" value={form.cost_excl_vat} onChange={(e) => handleCostExclChange(e.target.value)} data-testid="asset-form-cost" />
            </div>
            <div className="space-y-1">
              <Label>Cost (incl VAT)</Label>
              <Input type="number" step="0.01" value={form.cost_incl_vat} onChange={(e) => setForm({ ...form, cost_incl_vat: e.target.value })} />
            </div>

            <div className="col-span-2 mt-2 pt-3 border-t">
              <Label className="text-xs uppercase tracking-wider text-gray-500">SARS wear-and-tear</Label>
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Wear & tear category</Label>
              <Select value={form.wear_tear_key} onValueChange={handleWearTearChange}>
                <SelectTrigger data-testid="asset-form-wear-tear"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {wearTearRates.map((w) => (
                    <SelectItem key={w.key} value={w.key}>{w.label} ({w.years}yr)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Useful life (years)</Label>
              <Input type="number" min={1} value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Residual value (R)</Label>
              <Input type="number" step="0.01" value={form.residual_value} onChange={(e) => setForm({ ...form, residual_value: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="asset-form-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editingId ? "Save changes" : "Create asset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmDelete?.asset_tag} — {confirmDelete?.name}</strong> will be permanently removed from the register. Past depreciation already reported on the Income Statement will stay as historic entries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
